import React, { useState, useEffect, useCallback } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton } from '../waltero';
import { fetchLogAvailability } from '../../services/apiService';
import LogDetailsModal from './LogDetailsModal';

const SimpleLogsTable = ({ selectedOrgId, startDate, endDate }) => {
  const [logAvailability, setLogAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Generate date range (reverse chronological order like other tables)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);

    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];

  // Load log availability data
  const loadLogAvailability = useCallback(async () => {
    if (!selectedOrgId || !startDate || !endDate) {
      console.log('Missing required parameters for log availability');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`📊 Loading log availability for organization: ${selectedOrgId}`);
      const data = await fetchLogAvailability(selectedOrgId, startDate, endDate);
      setLogAvailability(data);
      console.log(`✅ Log availability loaded for ${data.devices?.length || 0} devices`);
    } catch (err) {
      console.error('❌ Error loading log availability:', err);
      setError('Failed to load log availability data');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, startDate, endDate]);

  // Load data when component mounts or dependencies change
  useEffect(() => {
    loadLogAvailability();
  }, [loadLogAvailability]);

  // Handle cell click
  const handleCellClick = useCallback((device, date, hasLogs) => {
    if (!hasLogs) {
      return; // Don't open modal if there are no logs
    }

    setSelectedDevice(device);
    setSelectedDate(date);
    setModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDevice(null);
    setSelectedDate(null);
  }, []);


  // Get log status for a device and date
  const getLogStatus = (device, date) => {
    if (!device.availability || !device.availability[date]) {
      return { hasLogs: false, logCount: 0 };
    }
    return device.availability[date];
  };

  return (
    <WalteroCard sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <WalteroTypography variant="h6">
          Device Logs Availability
        </WalteroTypography>
        {loading && (
          <CircularProgress size={16} />
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Device log availability status. Click on green cells to view detailed logs.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <WalteroButton size="small" onClick={loadLogAvailability} sx={{ ml: 2 }}>
            Retry
          </WalteroButton>
        </Alert>
      )}

      {!loading && !error && (!logAvailability || !logAvailability.devices || logAvailability.devices.length === 0) ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        logAvailability && logAvailability.devices && (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Device
                    </TableCell>
                    {sortedDates.map((date) => (
                      <TableCell key={date} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: '80px', textAlign: 'center' }}>
                        {date.slice(-5)} {/* Show MM-DD */}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logAvailability.devices.sort((a, b) => {
                    const aName = String(a.serialNumber || a.name || a.deviceId || '');
                    const bName = String(b.serialNumber || b.name || b.deviceId || '');
                    return aName.localeCompare(bName);
                  }).map((device) => (
                    <TableRow key={device.deviceId} hover>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          fontWeight: 'medium',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: '600',
                            fontSize: '13px'
                          }}
                          title={device.serialNumber || device.name || device.deviceId}
                        >
                          {device.serialNumber || device.name || device.deviceId}
                        </Typography>
                      </TableCell>
                      {sortedDates.map((date) => {
                        const logStatus = getLogStatus(device, date);
                        return (
                          <TableCell
                            key={date}
                            onClick={() => handleCellClick(device, date, logStatus.hasLogs)}
                            sx={{
                              textAlign: 'center',
                              backgroundColor: logStatus.hasLogs ? '#e8f5e8' : '#ffebee',
                              border: '1px solid #e0e0e0',
                              padding: '4px',
                              fontSize: '11px',
                              cursor: logStatus.hasLogs ? 'pointer' : 'default',
                              '&:hover': logStatus.hasLogs ? {
                                backgroundColor: '#c8e6c9',
                                transition: 'background-color 0.2s'
                              } : {}
                            }}
                            title={logStatus.hasLogs ? `${logStatus.logCount} logs - Click to view` : 'No logs'}
                          >
                            {logStatus.hasLogs ? (
                              <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✓</span>
                            ) : (
                              <span style={{ color: '#d32f2f' }}>-</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: '600' }}>
                Summary: {logAvailability.devices.length} devices, {logAvailability.statistics?.devicesWithLogs || 0} with logs, {sortedDates.length} days
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total logs: {logAvailability.statistics?.totalLogs?.toLocaleString() || 0}. Green cells indicate logs are available (click to view).
              </Typography>
            </Box>
          </>
        )
      )}

      {/* Log Details Modal */}
      {selectedDevice && selectedDate && (
        <LogDetailsModal
          open={modalOpen}
          onClose={handleModalClose}
          organizationId={selectedOrgId}
          device={selectedDevice}
          date={selectedDate}
        />
      )}
    </WalteroCard>
  );
};

export default SimpleLogsTable;